import axios from 'axios'

import { ChatMensagemModel } from './models/ChatMensagemModel'
import { ChatModel } from '../src/models/ChatModel'
import { ConteudoItemModel } from './models/ConteudoItemModel'
import { ConteudoPaginaModel } from './models/ConteudoPaginaModel'
import { CursoModel } from './models/CursoModel'
import { ForumMensagemModel } from './models/ForumMensagemModel'
import { ForumModel } from './models/ForumModel'
import { ModuloModel } from './models/ModuloModel'
import { ProgressoModel } from './models/ProgressoModel'
import { QuestaoModel } from './models/QuestaoModel'
import { UsuarioModel } from './models/UsuarioModel'
import { apiUrl } from './index'
import { DownloadModel } from './models/DownloadModel'
import {
  PortalArtigoModel,
  PortalEditorSectionPatchResponse,
  PortalEditorSectionTarget,
} from './models/PortalArtigoModel'

export interface IQueryParams {
  order?: 'ASC' | 'DESC'
  orderBy?: string
  orderByEntity?: string
  offset?: number
  limit?: number
}

export interface ICounter<T> {
  count: number
  rows: T[]
}

export interface PortalImagemUploadResponse {
  url: string
  key: string
  contentType: 'image/jpeg' | 'image/png' | 'image/webp' | 'image/avif'
  width: number
  height: number
}

const handleErrors = (err: any) => {
  if (err.response && err.response.data && err.response.data.message)
    err.message = err.response.data.message
  throw err
}

const responseBody = (res: any) => res.data

const requests = {
  delete: async (url: string, options?: any) =>
    await axios
      .delete(apiUrl + url, options)
      .then(responseBody)
      .catch(handleErrors),
  get: async (url: string, params?: any) =>
    await axios
      .get(apiUrl + url, { params })
      .then(responseBody)
      .catch(handleErrors),
  put: async (url: string, body: any) =>
    await axios
      .put(apiUrl + url, body)
      .then(responseBody)
      .catch(handleErrors),
  patch: async (url: string, body: any) =>
    await axios
      .patch(apiUrl + url, body)
      .then(responseBody)
      .catch(handleErrors),
  post: async (url: string, body: any, upload = false) => {
    const headers: any = upload ? { 'Content-Type': 'multipart/form-data' } : {}
    return await axios
      .post(apiUrl + url, body, { headers })
      .then(responseBody)
      .catch(handleErrors)
  },
}

const Usuarios = {
  login: async (
    cpf: string,
    senha: string
  ): Promise<{ usuario: any; matriculaId: number | null; alterarSenha: boolean }> =>
    await requests.post('/usuarios/login', { cpf, senha }),
  getSession: async (): Promise<{
    usuario: any
    matriculaId: number | null
    shouldChangePassword?: boolean
  }> => await requests.get('/usuarios/session'),
  definirSenha: async (novaSenha: string): Promise<boolean> =>
    await requests.post('/usuarios/altera-senha', { novaSenha }),
  logout: async (): Promise<void> => await requests.post('/usuarios/logout', {}),
  validateSenha: async (senha: string) =>
    await requests.post('/usuarios/validate-senha', { senha }),
  revalidaToken: async (usuarioId: number): Promise<{ token: string }> =>
    await requests.get(`/usuarios/revalida-token/${usuarioId}`),
  getByConta: async (contaId: number): Promise<UsuarioModel[]> =>
    await requests.get(`/usuarios/conta/${contaId}`),
  createOrUpdate: async (usuario: UsuarioModel): Promise<UsuarioModel> =>
    await requests.post('/usuarios', usuario),
  update: async (cpf: string, data: any): Promise<UsuarioModel> =>
    await requests.put(`/usuarios/${cpf}`, data),
  delete: async (id: string | number): Promise<UsuarioModel[]> =>
    await requests.delete(`/usuarios/${id}`),
  getByCpf: async (cpf: string): Promise<UsuarioModel> =>
    await requests.get(`/usuarios/cpf/${cpf}`),
  getCursos: async (alunoId: number): Promise<any> =>
    await requests.get(`/usuarios/cursos/${alunoId}`),
  updateSenha: async (
    usuarioId: number,
    senha: string,
    { senhaAtual, token }: { senhaAtual?: string; token?: string }
  ): Promise<boolean> =>
    await requests.put('/usuarios/senha', { usuarioId, senha, senhaAtual, token }),
  getByToken: async (token: string): Promise<UsuarioModel> =>
    await requests.get(`/usuarios/token/${token}`),
  createTokenRecuperaSenha: async (email: string): Promise<boolean> =>
    await requests.put('/usuarios/token', { email }),
  createTokenMudancaSenha: async (data: { usuarioId: number }): Promise<any> =>
    await requests.put('/usuarios/cria-token-mudanca-senha', data),
  deleteFromConta: async (usuarioId: number, contaId: number) =>
    await requests.delete(`/usuarios/${usuarioId}/conta/${contaId}`),
  getAllByPerfil: async (perfil: string, query: IQueryParams): Promise<ICounter<UsuarioModel>> =>
    await requests.get(`/usuarios/perfil/${perfil}`, query),
  get: async (params: { id: number }) => await requests.get(`/usuarios/${params.id}`),
  getTokens: async (userId: number) => await requests.get(`/usuarios/${userId}/busca-token`),
  getCertificados: async (userId: number) => await requests.get(`/usuarios/${userId}/certificados`),
}

const Files = {
  upload: async (formData: any): Promise<{ filepath: string }> =>
    await requests.post('/files', formData, true),
}

const Modulos = {
  get: async (query?: IQueryParams, cursoId?: number): Promise<ICounter<ModuloModel>> => {
    const params = {
      ...(query || {}),
      ...(cursoId ? { cursoId } : {}),
    }
    return await requests.get('/modulos/table', params)
  },
  post: async (modulo: Partial<ModuloModel>): Promise<ModuloModel> =>
    await requests.post('/modulos', modulo),
  delete: async (id: string | number): Promise<ModuloModel[]> =>
    await requests.delete(`/modulos/${id}`),
}

const UsuariosCursos = {
  post: async (usuario: Partial<UsuarioModel>): Promise<UsuarioModel[]> =>
    await requests.post('/usuarios', usuario),
  postMatricular: async (usuario: Partial<UsuarioModel>): Promise<any> =>
    await requests.post('/usuarios/pe/matricular', usuario),
  delete: async (id: string | number): Promise<UsuarioModel[]> =>
    await requests.delete(`/usuarios/${id}`),
}

const Matriculas = {
  get: async (matriculaId: number) => await requests.get(`/matriculas/${matriculaId}`),
  validar: async (
    data: Record<string, unknown>
  ): Promise<{ success: boolean; errorMessage?: string }> =>
    await requests.post('/matriculas/validar', data),
  aplicarCupom: async (data: { matriculaId: number; cupomCodigo: string }) =>
    await requests.post('/matriculas/aplicar-cupom', data),
  alterarValor: async (data: { matriculaId: number; valorNovo: string }) =>
    await requests.post('/matriculas/altera-valor', data),
  alterarPermissaoDescontoPix: async (data: { matriculaId: number; permiteDescontoPix: boolean }) =>
    await requests.put('/matriculas/permissao-desconto-pix', data),
  alterarPermissaoCertificado: async (data: { matriculaId: number; permissao: boolean }) =>
    await requests.put('/matriculas/permissao-certificado', data),
  detranConcluir: async (data: any) => await requests.post('/matriculas/detran-concluir', data),
  reemitirCobranca: async (matriculaId: number) =>
    await requests.post(`/matriculas/reemitir-cobranca/${matriculaId}`, {}),
  alterarMatriculaDetran: async (data: any) =>
    await requests.put('/matriculas/matricula-detran', data),
  emitirCertificado: async (matriculaId: number) =>
    await requests.post('/matriculas/emitir-certificado', { matriculaId }),
}

const Cursos = {
  get: async (query?: IQueryParams, id?: number): Promise<ICounter<CursoModel>> =>
    await requests.get(`/cursos/${id || ''}`, { ...(query || {}) }),
  getAll: async (query?: { includeSiteMetadata?: boolean }): Promise<CursoModel[]> =>
    await requests.get('/cursos/all', { ...(query || {}) }),
  post: async (curso: Partial<CursoModel>): Promise<CursoModel> =>
    await requests.post('/cursos', curso),
  delete: async (id): Promise<CursoModel> => await requests.delete(`/cursos/${id}`),
}

const Paginas = {
  novaPagina: async (moduloId: number): Promise<any> =>
    await requests.post(`/conteudos-paginas/nova-pagina/modulo/${moduloId}`, null),
  novaPaginaRascunho: async (paginaId: string, moduloId: number): Promise<any> =>
    await requests.post(
      `/conteudos-paginas/nova-pagina-rascunho/${paginaId}/modulo/${moduloId}`,
      null
    ),
  publicar: async (moduloId: number): Promise<any> =>
    await requests.put(`/conteudos-paginas/publicar/modulo/${moduloId}`, null),
  getByModulo: async (moduloId: number): Promise<ConteudoPaginaModel[]> =>
    await requests.get(`/conteudos-paginas/modulo/${moduloId}`),
  getConteudo: async (paginaId: string): Promise<any> =>
    await requests.get(`/conteudos-paginas/conteudo/${paginaId}`),
  createOrUpdate: async (
    paginas: ConteudoPaginaModel[],
    moduloId: number
  ): Promise<ConteudoPaginaModel[]> =>
    await requests.post('/conteudos-paginas', { paginas, moduloId }),
  getTotalItensPaginas: async (paginas: string[]): Promise<Array<{ id: string; total: number }>> =>
    await requests.post('/conteudos-paginas/get-totais', { paginas }),
  importar: async (moduloId: number, paginas: string[]): Promise<any> =>
    await requests.post(`/conteudos-paginas/importar/modulo/${moduloId}`, { paginas }),
  delete: async (paginaId: string): Promise<any> =>
    await requests.delete(`/conteudos-paginas/${paginaId}`),
  restaurar: async (paginaId: string): Promise<any> =>
    await requests.put(`/conteudos-paginas/restaurar/${paginaId}`, null),
}

const ConteudosItens = {
  create: async (
    moduloId: string,
    paginaId: string,
    tipo: string,
    conteudo: string
  ): Promise<ConteudoItemModel> =>
    await requests.post(`/conteudos-itens/modulo/${moduloId}/pagina/${paginaId}`, {
      conteudo,
      tipo,
    }),
  update: async (conteudoItemId: string, conteudo: any): Promise<ConteudoItemModel> =>
    await requests.put(`/conteudos-itens/conteudo/${conteudoItemId}`, { conteudo }),
  updateDuracao: async (conteudoItemId: string, duracao: number): Promise<ConteudoItemModel> =>
    await requests.put(`/conteudos-itens/duracao/${conteudoItemId}`, { duracao }),
  delete: async (conteudoItemId: string): Promise<boolean> =>
    await requests.delete(`/conteudos-itens/conteudo/${conteudoItemId}`),
  reorder: async ({ paginaId, from, to }): Promise<ConteudoItemModel[]> =>
    await requests.put('/conteudos-itens/reorder', { from, to, paginaId }),
}
const Live = {
  create: async (moduloId, conteudoId, data): Promise<ConteudoItemModel> =>
    await requests.post(`/live/modulo/${moduloId}/conteudo/${conteudoId}`, data),
  iniciar: async (moduloId, conteudoId): Promise<ConteudoItemModel> =>
    await requests.post(`/live/modulo/${moduloId}/conteudo/${conteudoId}/iniciar`, null),
}

const Questoes = {
  get: async (
    query?: IQueryParams,
    moduloId?: number,
    local?: string
  ): Promise<ICounter<QuestaoModel>> =>
    await requests.get('/questoes', { ...query, moduloId, local }),
  getById: async (questaoId: number): Promise<QuestaoModel> =>
    await requests.get(`/questoes/${questaoId}`),
  createOrUpdate: async (questao: QuestaoModel): Promise<QuestaoModel> =>
    await requests.post('/questoes', questao),
  delete: async (questaoId: string): Promise<void> =>
    await requests.delete(`/questoes/${questaoId}`),
}

const Foruns = {
  getAll: async (query?: IQueryParams): Promise<ICounter<ForumModel>> =>
    await requests.get('/usuarios/foruns/tutores', query),
  getForunsMensagensPendentes: async (
    query?: IQueryParams
  ): Promise<ICounter<ForumMensagemModel>> =>
    await requests.get('/foruns/mensagens/pendentes', query),
  getOne: async (forumId: number): Promise<ForumModel> => await requests.get(`/foruns/${forumId}`),
  getForunsMensagensByForum: async (forumId: number): Promise<ForumMensagemModel[]> =>
    await requests.get(`/foruns/mensagens/${forumId}`),
  aprovarMensagem: async (forumMensagemId: number): Promise<boolean> =>
    await requests.put('/foruns/mensagem/aprovar', { forumMensagemId }),
  aprovarForum: async (forumId: number): Promise<boolean> =>
    await requests.put('/foruns/aprovar', { forumId }),
  rejeitar: async (forumMensagemId: number): Promise<boolean> =>
    await requests.put('/foruns/mensagem/rejeitar', { forumMensagemId }),
}

const ForunsMensagens = {
  createOrUpdate: async (forumMensagem: ForumMensagemModel): Promise<ForumMensagemModel> =>
    await requests.post('/foruns/mensagens/', forumMensagem),
}

const Progresso = {
  getByAluno: async (alunoId: number, params?: any): Promise<ICounter<ProgressoModel>> =>
    await requests.get(`/progressos/aluno/${alunoId}`, params),
  getByMatricula: async (matriculaId: number): Promise<ICounter<ProgressoModel>> =>
    await requests.get(`/progressos/matricula/${matriculaId}`),
}

const Chat = {
  getChatsByUsuarioLogado: async (): Promise<ICounter<ChatModel>> => await requests.get('/chats'),
  getChatById: async (chatId: number): Promise<ChatModel> => await requests.get(`/chats/${chatId}`),
  getMessagesByChatId: async (chatId: number): Promise<ChatMensagemModel[]> =>
    await requests.get(`/chats/mensagens/${chatId}`),
  getAlunos: async (): Promise<ICounter<UsuarioModel>> => await requests.get('/chats/alunos'),
  sendMessage: async (mensagem: string, chatId: number): Promise<ChatMensagemModel> =>
    await requests.post('/chats/mensagens', { mensagem, chatId }),
  loadOrCreateChat: async (alunoId: number): Promise<ChatModel> =>
    await requests.post('/chats', { alunoId }),
  updateMensagensVisualizadas: async (chatId: number): Promise<boolean> =>
    await requests.put('/chats/mensagem/visualizada', { chatId }),
}

const DownloadsCursos = {
  get: async ({
    query,
    cursoId,
  }: {
    query: IQueryParams
    cursoId: number
  }): Promise<ICounter<DownloadModel>> =>
    await requests.get(`/downloads/curso/${cursoId}`, { ...(query || {}) }),
  create: async (formData: FormData): Promise<DownloadModel> =>
    await requests.post('/downloads/arquivo', formData, true),
  delete: async (downloadId: number): Promise<boolean> =>
    await requests.delete(`/downloads/${downloadId}`),
}

const Certificados = {
  getPendentes: async () => await requests.get(`/certificados/pendentes`),
  updateConfirmacaoEnvio: async (matriculaId: number) =>
    await requests.put(`/certificados/confirma-envio`, { matriculaId }),
}

const Isencao = {
  getIsencoes: (params) => requests.get('/isencoes', params),
}

const Pagamentos = {
  confirmarManual: (data: {
    matriculaId: number
    meioPagamento: 'PIX' | 'BOLETO' | 'CARTAO' | 'ISENTO'
    provedorPagamento?: 'MERCADO_PAGO' | 'EFI' | 'HYPERPAY' | null
    descricaoConfirmacao: string
  }) => requests.post('/pagamentos/confirmar-manual', data),
}

const Cupons = {
  get: (params) => requests.get('/cupons', params),
  getById: (cupomId) => requests.get(`/cupons/${cupomId}`),
  post: (cupom) => requests.post('/cupons', cupom),
}

const DetranLogs = {
  get: async (params: any) => await requests.get('/detran-logs', params),
  getMatricula: async (matriculaId: string) =>
    await requests.get(`/detran-logs/matricula/${matriculaId}`),
  concluir: async (data: any) => await requests.post('/detran-logs/actions/concluir', data),
  matricular: async (data: any) => await requests.post('/detran-logs/actions/matricular', data),
  cancelar: async (data: any) => await requests.post('/detran-logs/actions/cancelar', data),
}

const Validacoes = {
  update: async (data: any) => await requests.put('/validacoes', data),
}

const ValidacoesFaciais = {
  create: async (formData: FormData) => await requests.post('/validacoes-faciais', formData, true),
}

const EstatisticasDeEstudo = {
  getByMatricula: async (matriculaId: number) =>
    await requests.get(`/estatisticas-de-estudo/matriculas/${matriculaId}`),
}

const Localidades = {
  getMunicipios: async () => await requests.get('/localidades/municipios'),
}

const Turmas = {
  listAll: async (params?: any): Promise<any> => await requests.get('/turmas/all', params),
  getById: async (id: number): Promise<any> => await requests.get(`/turmas/id?turmaId=${id}`),
  getByCpfCnpj: async (cpfCnpj: string): Promise<any[]> =>
    await requests.get(`/turmas?cpfCnpj=${cpfCnpj}`),
  createOrUpdate: async (turma: any): Promise<any> => await requests.post('/turmas', turma),
  getValorCurso: async (params: any): Promise<any> =>
    await requests.get('/turmas/valor-curso', params),
  updateVencimento: async (turmaId: number, vencimento: string): Promise<any> =>
    await requests.put('/turmas/vencimento', { turmaId, vencimento, platform: 'PAINEL' }),
  importarAlunos: async (turmaId: number, alunos: any[]): Promise<any[]> =>
    await requests.post(`/turmas/${turmaId}/importar-alunos`, { alunos }),
  gerarCobranca: async (turmaId: number, tipo: 'PIX' | 'BOLETO'): Promise<any> =>
    await requests.post(`/turmas/${turmaId}/gerar-cobranca`, { tipo }),
  atualizarValores: async (turmaId: number, valores: { valorNewDriver?: number; valorCobranca?: number }): Promise<any> =>
    await requests.put(`/turmas/${turmaId}/valores`, valores),
  updateDadosPagador: async (turmaId: number, dadosPagador: any): Promise<any> =>
    await requests.put(`/turmas/${turmaId}/dados-pagador`, { dadosPagador }),
  contarSolicitacoes: async (): Promise<{ total: number }> =>
    await requests.get('/turmas/solicitadas/contagem'),
  concluirSolicitacao: async (turmaId: number): Promise<any> =>
    await requests.put(`/turmas/${turmaId}/status`, { statusTurma: 'ATIVA' }),
}

const Parceiros = {
  get: () => requests.get('/parceiros/lista'),
  list: async (): Promise<any[]> => await requests.get('/parceiros/lista'),
  getByCpfCnpj: async (cpfCnpj: string): Promise<any> =>
    await requests.get('/parceiros', { cpfCnpj }),
}

const NotasFiscais = {
  list: async (params: { page?: number; limit?: number }): Promise<any> =>
    await requests.get('/notas-fiscais', params),
  getByMatricula: async (matriculaId: number): Promise<any> =>
    await requests.get(`/notas-fiscais/by-matricula/${matriculaId}`),
  getTurmaMatriculas: async (turmaId: number): Promise<any[]> =>
    await requests.get(`/notas-fiscais/turma-matriculas/${turmaId}`),
  emitir: async (data: any): Promise<any> => await requests.post('/notas-fiscais', data),
  refreshStatus: async (id: number): Promise<any> =>
    await requests.get(`/notas-fiscais/${id}/status`),
  cancelar: async (id: number): Promise<any> =>
    await requests.post(`/notas-fiscais/${id}/cancelar`, {}),
  getNonce: async (plugNotasId: string): Promise<{ nonce: string }> =>
    await requests.get(`/notas-fiscais/download/${plugNotasId}`),
  buscarCnpj: async (cnpj: string): Promise<any> =>
    await fetch(`https://brasilapi.com.br/api/cnpj/v1/${cnpj}`).then((r) => r.json()),
  buscarCep: async (cep: string): Promise<any> =>
    await fetch(`https://viacep.com.br/ws/${cep}/json`).then((r) => r.json()),
}

const PortalArtigos = {
  get: async (
    params?: IQueryParams & { status?: string; busca?: string; page?: number }
  ): Promise<ICounter<PortalArtigoModel>> => await requests.get('/portal-artigos', params),
  getById: async (id: number): Promise<PortalArtigoModel> =>
    await requests.get(`/portal-artigos/${id}`),
  create: async (artigo: PortalArtigoModel): Promise<PortalArtigoModel> =>
    await requests.post('/portal-artigos', artigo),
  update: async (id: number, artigo: PortalArtigoModel): Promise<PortalArtigoModel> =>
    await requests.put(`/portal-artigos/${id}`, artigo),
  uploadImage: async (file: File): Promise<PortalImagemUploadResponse> => {
    const formData = new FormData()
    formData.append('file', file)
    return await requests.post('/portal-artigos/imagens', formData, true)
  },
  updateEditorSection: async (
    id: number,
    target: PortalEditorSectionTarget,
    value: unknown,
    expectedUpdatedAt: string
  ): Promise<PortalEditorSectionPatchResponse> =>
    await requests.patch(`/portal-artigos/${id}/editor-section`, {
      expectedUpdatedAt,
      target,
      value,
    }),
  delete: async (id: number): Promise<boolean> => await requests.delete(`/portal-artigos/${id}`),
}

export default {
  Usuarios,
  UsuariosCursos,
  Files,
  Modulos,
  Matriculas,
  Cursos,
  Paginas,
  Questoes,
  Foruns,
  ForunsMensagens,
  Progresso,
  Chat,
  ConteudosItens,
  Live,
  DownloadsCursos,
  Certificados,
  Isencao,
  Pagamentos,
  Cupons,
  Parceiros,
  DetranLogs,
  Validacoes,
  ValidacoesFaciais,
  EstatisticasDeEstudo,
  Localidades,
  Turmas,
  NotasFiscais,
  PortalArtigos,
}
